import React, { useState, useEffect, useCallback } from 'react';
import { inputStyle, btnPrimary, btnSm, Card, fmtKES } from './components/UI';
import * as api from './api/client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LoginPage         from './pages/LoginPage';
import AdminPage         from './pages/AdminPage';
import WaiterPage        from './pages/WaiterPage';
import CashierPage       from './pages/CashierPage';
import LeaveRequestPage from './pages/LeaveRequestPage';
import KitchenPage       from './pages/KitchenPage';
import ReceptionPage     from './pages/ReceptionPage';
import ReservationsPage  from './pages/ReservationsPage';
import ConferencePage    from './pages/ConferencePage';
import GatePage          from './pages/GatePage';
import GuestFolioPage    from './pages/GuestFolioPage';
import HousekeepingPage  from './pages/HousekeepingPage';
import AccountantPage    from './pages/AccountantPage';
import ChefPage          from './pages/ChefPage';
import GiftShopPage      from './pages/GiftShopPage';
import RestaurantManagerPage from './pages/RestaurantManagerPage';
import AuditTrailPage    from './pages/AuditTrailPage';
import LandingPage       from './pages/LandingPage';
import SwimmingPage     from './pages/SwimmingPage';
import MarketingPage    from './pages/MarketingPage';
import SeoPage          from './pages/SeoPage';
import PayrollPage      from './pages/PayrollPage';
import StorekeeperPage  from './pages/StorekeeperPage';
import HRPage           from './pages/HRPage';
import SwimmingAttendantPage from './pages/SwimmingAttendantPage';
import KidsAmenitiesPage from './pages/KidsAmenitiesPage';
import GMDashboardPage    from './pages/GMDashboardPage';
import GeneralLedgerPage  from './pages/GeneralLedgerPage';
import VendorAPPage       from './pages/VendorAPPage';
import CRMPage            from './pages/CRMPage';
import MaintenancePage    from './pages/MaintenancePage';
import DriverPage         from './pages/DriverPage';
import Layout            from './components/Layout';
import NotificationsPanel from './components/NotificationsPanel';
import { Toast }         from './components/UI';
import HowToGuide, { guideKey } from './components/HowToGuide';

function AppInner() {
  const { user, loading } = useAuth();
  const [toast, setToast]         = useState(null);
  const [showSystem, setShowSystem] = useState(false);

  // Keep hash in sync when it changes (e.g. user types /#/staff in browser)
  React.useEffect(() => {
    const onHash = () => { if (window.location.hash === '#/staff') setShowSystem(true); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const [tab, setTab]             = useState('main');
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastRolloverDate, setLastRolloverDate] = useState(null);

  const notify = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Poll notifications every 30s — update count + browser tab badge
  const checkNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.checkNotifications();
      const count = r.data.unread_count || 0;
      setUnreadCount(count);
      // Update browser tab title with unread badge
      document.title = count > 0
        ? `(${count}) Grand Memories`
        : 'Grand Memories Resort & Spa';
    } catch(e) {}
  }, [user]);

  useEffect(() => {
    if (!user) return;
    checkNotifs();
    const iv = setInterval(checkNotifs, 30000);
    return () => clearInterval(iv);
  }, [checkNotifs]);

  // Midnight rollover for admin/accountant
  useEffect(() => {
    if (!user || !['admin','accountant'].includes(user.role)) return;
    const scheduleRollover = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 30, 0); // 12:00:30 AM
      const msToMidnight = midnight - now;
      const todayStr = now.toISOString().slice(0,10);
      if (lastRolloverDate === todayStr) return;
      const timer = setTimeout(async () => {
        try {
          await api.performRollover();
          setLastRolloverDate(new Date().toISOString().slice(0,10));
          notify('Daily rollover completed automatically at midnight');
          checkNotifs();
        } catch(e) {}
      }, msToMidnight > 0 ? msToMidnight : 1000);
      return timer;
    };
    const t = scheduleRollover();
    return () => t && clearTimeout(t);
  }, [user, lastRolloverDate]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'var(--bg, #080808)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--primary, #c8a96e)', fontSize:18, fontFamily:"'Cormorant Garamond',serif", letterSpacing:4 }}>
      ◈  Loading…
    </div>
  );

  // Hash-based routing: /#/staff → login directly, no button on landing page
  const isStaffRoute = window.location.hash === '#/staff' || window.location.hash.startsWith('#/staff');

  if (!user && (isStaffRoute || showSystem)) {
    return <LoginPage onBack={() => {
      setShowSystem(false);
      window.location.hash = '';
    }} />;
  }
  if (!user) return <LandingPage />;

  const role        = user.role;
  const isAdmin     = role === 'admin';
  const isReception = ['admin','reception'].includes(role);
  const isHK        = ['admin','housekeeping'].includes(role);
  const isChef      = ['admin','kitchen','chef'].includes(role);
  const isGiftShop  = ['admin','gift_shop'].includes(role);
  const isMgr       = role === 'restaurant_manager';
  const canConf     = ['admin','reception','cashier'].includes(role);
  const canGate     = ['admin','gate'].includes(role);
  const canFolio    = ['admin','reception','cashier'].includes(role);
  const canAcct     = ['admin','accountant'].includes(role);
  const canAudit    = ['admin','accountant','restaurant_manager'].includes(role);
  const canMarketing= ['admin','marketing'].includes(role);
  const canHR       = ['admin','hr'].includes(role);
  const canSwim       = ['admin','reception','cashier'].includes(role);
  const isSwimAttendant = ['admin','swimming_attendant'].includes(role);
  const isKidsAmenities = ['admin','kids_amenities'].includes(role);
  const canStore      = ['admin','storekeeper'].includes(role);

  const mainLabel = {
    admin:'⚙️ Admin', reception:'🏨 Reception', cashier:'💳 Cashier',
    waiter:'🍽️ Waiter', kitchen:'👨‍🍳 Kitchen', gate:'🔐 Gate',
    housekeeping:'🧹 Housekeeping', accountant:'📊 Accounting',
    marketing:'📣 Marketing',
    hr:'👥 HR',
    chef:'👨‍🍳 Chef', gift_shop:'🛍️ Gift Shop', restaurant_manager:'🍽️ Restaurant',
    storekeeper:'🏪 Storekeeper',
    swimming_attendant:'🏊 Pool Attendant',
    kids_amenities:'🧒 Kids Zone',
    driver:'🚗 Driver',
    general_manager:'📊 GM Dashboard',
  }[role] || role;

  // ── Tab definitions grouped by section ─────────────────────────────────────
  // Operations tabs
  const opsTabs = [
    { id:'main',         label: mainLabel },
    isReception && { id:'reservations', label:'📅 Reservations' },
    canFolio    && { id:'folio',        label:'📒 Folios' },
    canGate     && { id:'gate',         label:'🔐 Gate' },
    isHK        && { id:'housekeeping', label:'🧹 Housekeeping' },
    isChef      && { id:'costing',      label:'👨‍🍳 Food Costing' },
    (isAdmin||isReception||isGiftShop) && { id:'giftshop', label:'🛍️ Gift Shop' },
    canSwim    && { id:'swimming',     label:'🏊 Pool & Kids' },
    canStore   && { id:'store',        label:'🏪 Storeroom' },
    { id:'attendance', label:'🕐 Attendance' },
    { id:'leave',      label:'📅 My Leave' },
  ].filter(Boolean);

  // Accounting tabs
  const acctTabs = [
    canAcct     && { id:'accounting',    label:'📊 Accounting' },
    canAcct     && { id:'general_ledger',label:'📒 General Ledger' },
    canAcct     && { id:'vendors_ap',    label:'🏭 Vendors & AP' },
    canAudit    && { id:'audit',         label:'🔍 Audit Trail' },
  ].filter(Boolean);

  // Marketing tabs
  const mktTabs = [
    canMarketing && { id:'marketing',   label:'📣 Marketing' },
    canMarketing && { id:'seo',         label:'🎯 SEO Intelligence' },
    (isAdmin||canAcct||canMarketing) && { id:'crm', label:'👤 Guest CRM' },
  ].filter(Boolean);

  // HR / Payroll tabs
  const hrTabs = [
    canHR && { id:'payroll', label:'💼 Payroll' },
  ].filter(Boolean);

  // Admin / Executive tabs
  const adminTabs = [
    isAdmin && { id:'gm_dashboard', label:'📊 GM Dashboard' },   // GM's own main screen is the dashboard
    isAdmin && { id:'maintenance',  label:'🔧 Maintenance' },
  ].filter(Boolean);

  // Roles that see only their primary page with no extra tabs
  const primaryOnly = ['waiter','kitchen','gate','housekeeping','gift_shop','restaurant_manager','chef','storekeeper','marketing','hr','swimming_attendant','kids_amenities','driver'].includes(role);

  // All tabs flattened (for tab rendering logic)
  const extraTabs = [
    ...opsTabs.slice(1), // skip 'main' — already in opsTabs[0]
    ...acctTabs,
    ...mktTabs,
    ...hrTabs,
    ...adminTabs,
  ];

  // Strip emoji from labels for clean tab display
  const cleanLabel = (label) => label.replace(/[\u{1F000}-\u{1FFFF}]|[\u2600-\u27FF]\uFE0F?|[\uD800-\uDBFF][\uDC00-\uDFFF]/gu, '').trim();

  // ── SVG icon paths for sidebar nav ──────────────────────────
  const NAV_ICONS = {
    main_admin:    ['M3 13h8V3H3v10z','M13 21h8v-8h-8v8z','M13 3v6h8V3h-8z','M3 21v-4h8v4H3z'],
    main_reception:['M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z','M9 22V12h6v10'],
    main_cashier:  ['M12 1v22','M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
    main_waiter:   ['M18 8h1a4 4 0 010 8h-1','M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z','M6 1v3','M10 1v3','M14 1v3'],
    main_kitchen:  ['M18 8h1a4 4 0 010 8h-1','M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z'],
    main_gate:     ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    main_housekeeping:['M3 22h18','M3 22l5-5','M17 3l4 4-10 10-5.5-1.5L4 11 17 3z'],
    main_accountant:['M18 20V10','M12 20V4','M6 20v-6'],
    main_chef:     ['M18 8h1a4 4 0 010 8h-1','M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z'],
    main_gift_shop:['M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z','M3 6h18','M16 10a4 4 0 01-8 0'],
    main_restaurant_manager:['M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z'],
    main_storekeeper:['M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z'],
    main_marketing: ['M3 11l19-9-9 19-2-8-8-2z'],
    main_hr:        ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2','M9 7a4 4 0 100 8 4 4 0 000-8z'],
    main_swimming_attendant:['M2 12c.6.5 1.2 1 2.5 1C7 13 7 11 9.5 11s2.5 2 5 2 2.5-2 5-2 2.5 2 2.5 2'],
    main_kids_amenities:['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2'],
    main_driver:['M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v10a2 2 0 01-2 2h-2','M17 21a2 2 0 100-4 2 2 0 000 4z','M7 21a2 2 0 100-4 2 2 0 000 4z'],
    reservations:  ['M8 6h13','M8 12h13','M8 18h13','M3 6h.01','M3 12h.01','M3 18h.01'],
    folio:         ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z','M14 2v6h6','M16 13H8','M16 17H8'],
    gate:          ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    housekeeping:  ['M3 22h18','M3 22l5-5','M17 3l4 4-10 10'],
    accounting:    ['M18 20V10','M12 20V4','M6 20v-6'],
    general_ledger:['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z','M14 2v6h6','M16 13H8','M16 17H8'],
    vendors_ap:    ['M3 3h18v4H3z','M3 9h18v4H3z','M3 15h18v4H3z'],
    crm:           ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2','M9 7a4 4 0 100 8 4 4 0 000-8z','M23 21v-2a4 4 0 00-3-3.87','M16 3.13a4 4 0 010 7.75'],
    gm_dashboard:  ['M18 20V10','M12 20V4','M6 20v-6','M22 20H2'],
    maintenance:   ['M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z'],
    costing:       ['M18 8h1a4 4 0 010 8h-1','M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z'],
    giftshop:      ['M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z','M3 6h18'],
    audit:         ['M9 11l3 3L22 4','M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11'],
    marketing:     ['M3 11l19-9-9 19-2-8-8-2z'],
    payroll:       ['M12 1v22','M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
    swimming:      ['M2 12c.6.5 1.2 1 2.5 1C7 13 7 11 9.5 11s2.5 2 5 2 2.5-2 5-2 2.5 2 2.5 2'],
    store:         ['M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z'],
    attendance:    ['M12 2a10 10 0 100 20A10 10 0 0012 2z','M12 6v6l4 2'],
  };

  const NavIcon = ({ d, size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.7} strokeLinecap="round"
      strokeLinejoin="round" style={{ flexShrink:0 }}>
      {Array.isArray(d) ? d.map((p,i)=><path key={i} d={p}/>) : <path d={d}/>}
    </svg>
  );

  const NAV_LABELS = {
    main: cleanLabel(mainLabel),
    reservations:'Reservations', folio:'Guest Folios', gate:'Gate Log',
    housekeeping:'Housekeeping', accounting:'Accounting', costing:'Food Costing',
    giftshop:'Gift Shop', audit:'Audit Trail', marketing:'Marketing',
    payroll:'Payroll', swimming:'Pool & Kids', store:'Storeroom',
    general_ledger:'General Ledger', vendors_ap:'Vendors & AP',
    crm:'Guest CRM', gm_dashboard:'GM Dashboard', maintenance:'Maintenance',
    attendance:'Attendance',
  };

  // Helper to render a nav button
  const NavBtn = ({ t }) => {
    const isActive = tab === t.id;
    const iconKey = t.id === 'main' ? `main_${role}` : t.id;
    const d = NAV_ICONS[iconKey] || ['M3 13h8V3H3v10z','M13 21h8v-8h-8v8z'];
    const label = NAV_LABELS[t.id] || cleanLabel(t.label);
    return (
      <button key={t.id} onClick={() => setTab(t.id)}
        className={`nav-item${isActive ? ' active' : ''}`}
        title={label}
      >
        <span className="nav-item-icon" style={{ color: isActive ? '#1B3A2D' : 'rgba(255,255,255,0.65)' }}>
          <NavIcon d={d} size={15}/>
        </span>
        <span className="nav-item-label">{label}</span>
      </button>
    );
  };

  const sideContent = primaryOnly ? (
    <>
      <div className="nav-section">My Dashboard</div>
      <NavBtn t={{ id:'main', label: mainLabel }} />
      <div className="nav-section">My Account</div>
      <NavBtn t={{ id:'leave',      label:'📅 My Leave' }} />
      <NavBtn t={{ id:'attendance', label:'🕐 Attendance' }} />
    </>
  ) : (
    <>
      {/* ── Operations ── */}
      <div className="nav-section">Hotel Operations</div>
      {opsTabs.map(t => <NavBtn key={t.id} t={t} />)}

      {/* ── Accounting ── */}
      {acctTabs.length > 0 && (
        <>
          <div className="nav-section">Accounting</div>
          {acctTabs.map(t => <NavBtn key={t.id} t={t} />)}
        </>
      )}

      {/* ── Marketing ── */}
      {mktTabs.length > 0 && (
        <>
          <div className="nav-section">Marketing</div>
          {mktTabs.map(t => <NavBtn key={t.id} t={t} />)}
        </>
      )}

      {/* ── HR ── */}
      {hrTabs.length > 0 && (
        <>
          <div className="nav-section">HR</div>
          {hrTabs.map(t => <NavBtn key={t.id} t={t} />)}
        </>
      )}

      {/* ── Admin / Executive ── */}
      {adminTabs.length > 0 && (
        <>
          <div className="nav-section">Admin</div>
          {adminTabs.map(t => <NavBtn key={t.id} t={t} />)}
        </>
      )}
    </>
  );

  return (
    <Layout
      onNotifClick={() => { setNotifOpen(true); setUnreadCount(0); }}
      notifCount={unreadCount}
      sideContent={sideContent}
    >
      <Toast toast={toast} />
      <NotificationsPanel
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        role={role}
        onNavigate={(targetTab) => {
          // Map the related_tab value to the actual tab IDs used in App.js
          const TAB_MAP = {
            store:              canStore      ? 'store'              : null,
            accounting:         canAcct       ? 'accounting'         : null,
            payroll:            canHR         ? 'payroll'            : null,
            costing:            isChef        ? 'costing'            : null,
            main:               'main',
            attendance:         'attendance',
            gm_dashboard: isAdmin ? 'gm_dashboard' : (role === 'general_manager' ? 'main' : null),
            reservations:       isReception   ? 'reservations'       : null,
            folio:              canFolio      ? 'folio'              : null,
            vendors_ap:         canAcct       ? 'vendors_ap'         : null,
            leave:              'leave',
            // Approval-specific routes
            restaurant_manager: (isAdmin || isMgr) ? 'main'         : null,
            general_ledger:     canAcct       ? 'general_ledger'     : null,
            maintenance:        isAdmin       ? 'maintenance'        : null,
            crm:                (isAdmin||canAcct) ? 'crm'           : null,
          };
          const resolved = TAB_MAP[targetTab] ?? targetTab;
          if (resolved) { setTab(resolved); setNotifOpen(false); }
        }}
      />

      {/* Tabs rendered in sidebar via sideContent */}

      {tab === 'reservations' && isReception  && <ReservationsPage  notify={notify} />}
      {tab === 'folio'        && canFolio      && <GuestFolioPage    notify={notify} />}
      {tab === 'gate'         && canGate       && <GatePage          notify={notify} />}
      {tab === 'housekeeping' && isHK          && <HousekeepingPage  notify={notify} />}
      {tab === 'accounting'   && canAcct       && <AccountantPage    notify={notify} />}
      {tab === 'costing'      && isChef        && <ChefPage          notify={notify} />}
      {tab === 'giftshop'     && (isAdmin||isReception||isGiftShop) && <GiftShopPage notify={notify} userRole={role} />}
      {tab === 'audit'        && canAudit      && <AuditTrailPage    notify={notify} isAdmin={isAdmin} />}
      {tab === 'marketing'    && canMarketing  && <MarketingPage     notify={notify} />}
      {tab === 'seo'          && canMarketing  && <SeoPage           notify={notify} />}
      {tab === 'leave'        &&                  <LeaveRequestPage   notify={notify} />}
      {tab === 'attendance'   &&                  <AttendanceSelfService notify={notify} userRole={role} />}
      {tab === 'payroll'      && canHR            && <PayrollPage    notify={notify} userRole={role} />}
      {tab === 'swimming'     && canSwim       && <SwimmingPage      notify={notify} />}
      {tab === 'store'        && canStore      && <StorekeeperPage   notify={notify} />}
      {tab === 'gm_dashboard' && ['admin','general_manager'].includes(role) && <GMDashboardPage   notify={notify} />}
      {tab === 'general_ledger' && canAcct      && <GeneralLedgerPage  notify={notify} userRole={role} />}
      {tab === 'vendors_ap'    && canAcct       && <VendorAPPage       notify={notify} userRole={role} />}
      {tab === 'crm'           && (isAdmin||canAcct||canMarketing) && <CRMPage       notify={notify} userRole={role} />}
      {tab === 'maintenance'   && isAdmin        && <MaintenancePage   notify={notify} userRole={role} />}

      {tab === 'main' && (
        <>
          {role === 'admin'        && <AdminPage        notify={notify} />}
          {role === 'waiter'       && <WaiterPage       notify={notify} />}
          {role === 'cashier'      && <CashierPage      notify={notify} />}
          {role === 'kitchen'      && <KitchenPage      notify={notify} />}
          {role === 'chef'         && <ChefPage          notify={notify} />}
          {role === 'reception'    && <ReceptionPage    notify={notify} />}
          {role === 'gate'         && <GatePage         notify={notify} />}
          {role === 'housekeeping' && <HousekeepingPage notify={notify} />}
          {role === 'accountant'   && <AccountantPage   notify={notify} />}
          {role === 'gift_shop'         && <GiftShopPage           notify={notify} userRole={role} />}
          {role === 'restaurant_manager' && <RestaurantManagerPage   notify={notify} />}
          {role === 'storekeeper'  && <StorekeeperPage  notify={notify} />}
          {role === 'marketing'   && <MarketingPage    notify={notify} />}
          {role === 'hr'          && <HRPage           notify={notify} />}
          {role === 'swimming_attendant' && <SwimmingAttendantPage notify={notify} />}
          {role === 'kids_amenities'     && <KidsAmenitiesPage     notify={notify} />}
          {role === 'driver'           && <DriverPage          notify={notify} />}
          {role === 'general_manager'  && <GMDashboardPage     notify={notify} />}
        </>
      )}

      {/* Role- and screen-specific help at the bottom of every page */}
      {/* Admin's main screen shows its own per-section guide inside the Admin Panel */}
      {!(role === 'admin' && tab === 'main') && (
        <HowToGuide key={guideKey(tab, role) || 'none'} tab={tab} role={role} basics={tab === 'main'} />
      )}
    </Layout>
  );
}

// ── Employee Self-Service Attendance Widget ────────────────────────────────
function AttendanceSelfService({ notify }) {
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const STATUS_COLOR = { present:'#2ecc71', absent:'#e74c3c', half_day:'#e67e22', leave:'#3498db', late:'#f39c12' };

  const loadToday = useCallback(async () => {
    try {
      const r = await api.getTodayAttendance();
      setToday(r.data.status === 'not_clocked_in' ? null : r.data);
    } catch(e) {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.getAttendance('?');
      setHistory((r.data.results || r.data).slice(0, 30));
    } catch(e) {}
  }, []);

  useEffect(() => { loadToday(); }, [loadToday]);

  const doClockIn = async () => {
    setLoading(true);
    try { await api.clockIn(); notify('Clocked in successfully!'); loadToday(); }
    catch(e) { notify(e.response?.data?.error || 'Error clocking in', 'error'); }
    finally { setLoading(false); }
  };

  const doClockOut = async () => {
    setLoading(true);
    try { await api.clockOut(); notify('Clocked out successfully!'); loadToday(); }
    catch(e) { notify(e.response?.data?.error || 'Error clocking out', 'error'); }
    finally { setLoading(false); }
  };

  const dateStr = new Date().toLocaleDateString('en-KE', {weekday:'long', year:'numeric', month:'long', day:'numeric'});
  const timeStr = new Date().toLocaleTimeString('en-KE', {hour:'2-digit', minute:'2-digit'});

  return (
    <div style={{maxWidth:560, margin:'0 auto'}}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:22, fontWeight:800, color:'var(--textPrimary)'}}>Clock In / Out</div>
        <div style={{fontSize:13, color:'var(--textMuted)', marginTop:2}}>{dateStr}</div>
      </div>

      <Card style={{marginBottom:14}}>
        <div style={{textAlign:'center', padding:'24px 0'}}>
          <div style={{fontSize:52, fontWeight:900, color:'var(--primary)', fontFamily:'monospace', letterSpacing:3, marginBottom:8}}>{timeStr}</div>

          {today ? (
            <div>
              <div style={{margin:'0 auto 16px', display:'inline-flex', alignItems:'center', gap:8, padding:'7px 18px', background:'#2ecc7115', border:'1px solid #2ecc7144', borderRadius:30}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:'#2ecc71'}}/>
                <span style={{color:'#2ecc71',fontWeight:700,fontSize:13}}>Clocked in at {today.check_in_time ? today.check_in_time.slice(0,5) : '?'}</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,margin:'0 0 20px 0'}}>
                {[['Clock In', today.check_in_time ? today.check_in_time.slice(0,5) : '—', '#2ecc71'],
                  ['Clock Out', today.check_out_time ? today.check_out_time.slice(0,5) : '—', '#e74c3c'],
                  ['Hours', today.hours_worked ? today.hours_worked+'h' : '—', '#c8a96e']
                ].map(([l,v,c])=>(
                  <div key={l} style={{background:'var(--bgHover)',borderRadius:8,padding:'12px 6px'}}>
                    <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>{l}</div>
                    <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
                  </div>
                ))}
              </div>
              {!today.check_out_time ? (
                <button style={{...btnPrimary,background:'#e74c3c',maxWidth:220,margin:'0 auto'}} onClick={doClockOut} disabled={loading}>
                  {loading ? 'Processing…' : 'Clock Out'}
                </button>
              ) : (
                <div style={{color:'var(--textMuted)',fontSize:13}}>Shift complete for today</div>
              )}
            </div>
          ) : (
            <div>
              <div style={{margin:'0 auto 20px',display:'inline-flex',alignItems:'center',gap:8,padding:'7px 18px',background:'#e74c3c15',border:'1px solid #e74c3c44',borderRadius:30}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:'#e74c3c'}}/>
                <span style={{color:'#e74c3c',fontWeight:700,fontSize:13}}>Not clocked in today</span>
              </div>
              <br/>
              <button style={{...btnPrimary,background:'#2ecc71',maxWidth:220,margin:'0 auto'}} onClick={doClockIn} disabled={loading}>
                {loading ? 'Processing…' : 'Clock In Now'}
              </button>
            </div>
          )}
        </div>
      </Card>

      <button style={{...btnSm,width:'100%',background:'var(--bgCard)',marginBottom:12}}
        onClick={()=>{ setShowHistory(h=>!h); if (!showHistory) loadHistory(); }}>
        {showHistory ? 'Hide History' : 'View My Attendance History'}
      </button>

      {showHistory && (
        <Card title="Recent Attendance">
          {history.length===0 ? <div style={{color:'var(--textFaint)',padding:12,fontSize:13}}>No records</div> :
            history.map((r,i)=>(
              <div key={r.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:i<history.length-1?'1px solid var(--border)':'none'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:'var(--textPrimary)'}}>{r.date}</div>
                  <div style={{fontSize:11,color:'var(--textFaint)'}}>{r.check_in_time ? r.check_in_time.slice(0,5) : '—'} → {r.check_out_time ? r.check_out_time.slice(0,5) : '—'}{r.hours_worked ? ' ('+r.hours_worked+'h)' : ''}</div>
                </div>
                <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,background:(STATUS_COLOR[r.status]||'var(--textMuted)')+'22',color:STATUS_COLOR[r.status]||'var(--textMuted)'}}>
                  {r.status ? r.status.replace('_',' ') : ''}
                </span>
              </div>
            ))}
        </Card>
      )}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ThemeProvider>
  );
}
